import * as React from 'react';
import {
  EntityFormControl,
  useEntityDetailsProps,
  EntityFormControlProps,
  AddItemModalTriggers,
  AddEntityReferenceFromListModal,
  useReferencedByDomainEntityIdData,
} from '@web-config-app/entity-form';
import {
  EntityLoader,
  useConfigApp,
  useConfigAppParams,
  useConfigAppPath,
} from '@web-config-app/core-react';
import {
  type DomainEntity,
  type EntityDetail,
  findEntityModulePath,
  ENTITY_TYPE_TO_ASSET_TYPE,
  ASSET_TYPE_TO_ENTITY_TYPE,
  getEntityInstanceName,
} from '@web-config-app/core';
import { useIntl } from '@leagueplatform/locales';
import {
  Box,
  Button,
  InputStatusMessage,
  Spinner,
  StackLayout,
} from '@leagueplatform/genesis-core';
import { LeadingThumbnail } from '@web-config-app/core-react-ui';
import { EntitySelectValue } from '../internals/entity-select-value.component';
import { useEntityReferenceControlState } from '../internals/use-entity-reference-control-state';
import { useEntityReferenceControlContext } from '../internals/use-entity-reference-control-context';
import { EntitySelectModalHeading } from '../internals/entity-select-modal-heading.component';
import {
  getAssetDescriptionFromDomainEntityAttributes,
  getAssetTitleFromDomainEntityAttributes,
  getAssetUrlFromDomainEntityAttributes,
} from '../../../../utils/get-asset-fields-from-domain-entity-attributes/get-asset-fields-from-domain-entity-attributes';

interface MediaReferenceContentsProps extends EntityFormControlProps {
  referencedEntityConfig: DomainEntity;
}

const MediaReferenceContents: React.FC<MediaReferenceContentsProps> = ({
  handleChange,
  path,
  data,
  referencedEntityConfig,
  enabled,
}) => {
  const { formatMessage } = useIntl();
  const isArrayItem = useEntityReferenceControlContext();
  const { currentAuthoringEnvironment } = useEntityDetailsProps();
  const { modules = [], entityConfigs } = useConfigApp();
  const { environmentKey } = useConfigAppParams();
  const generateConfigPaths = useConfigAppPath();
  const entityModulePath = findEntityModulePath(
    modules,
    referencedEntityConfig.type,
    entityConfigs,
  );
  const { getEntityDetailsPathForId } = generateConfigPaths({
    tabId: entityModulePath?.tabId,
    moduleId: entityModulePath?.moduleId,
    childModuleId: entityModulePath?.childModuleId,
    environmentKey,
  });

  const {
    referencedEntity: entityReferenceData,
    isLoading,
    isError,
  } = useReferencedByDomainEntityIdData({
    domainEntity: referencedEntityConfig,
    domainEntityId: data,
    environment: currentAuthoringEnvironment,
  });

  const {
    closeChooseFromList,
    openChooseFromList,
    isAddingFromList,
    addItemTriggerList,
  } = useEntityReferenceControlState({ domainEntity: referencedEntityConfig });

  const onSubmitChooseFromList = (entityDetail: EntityDetail) => {
    handleChange(path, entityDetail.attributes.entityMetadata.domainEntityId);
    closeChooseFromList();
  };

  const onClearEntityReference = () => {
    handleChange(path, undefined);
  };

  const mediaUrl = getAssetUrlFromDomainEntityAttributes(
    entityReferenceData?.attributes?.domainEntityAttributes,
  );
  const mediaTitle = getAssetTitleFromDomainEntityAttributes(
    entityReferenceData?.attributes?.domainEntityAttributes,
  );
  const mediaDescription = getAssetDescriptionFromDomainEntityAttributes(
    entityReferenceData?.attributes?.domainEntityAttributes,
  );
  const secondaryText =
    entityReferenceData?.attributes?.entityMetadata?.domainEntityId;

  const hasData = Boolean(data);
  const isLoadingEntityReference = hasData && isLoading && !entityReferenceData;
  const shouldDisplayError =
    hasData && !isLoading && (isError || !entityReferenceData);
  const shouldDisplayAddImage = !hasData && !isLoading && !entityReferenceData;

  return (
    <>
      <Box css={{ width: '100%' }}>
        <div aria-live="polite">
          {isLoadingEntityReference && (
            <StackLayout css={{ width: '100%' }} horizontalAlignment="center">
              <Spinner loading={isLoadingEntityReference} />
            </StackLayout>
          )}
          {entityReferenceData ? (
            <EntitySelectValue
              title={getEntityInstanceName(
                entityReferenceData,
                referencedEntityConfig,
              )}
              href={getEntityDetailsPathForId(entityReferenceData.id)}
              leadingThumbnail={
                <LeadingThumbnail
                  assetType={
                    ENTITY_TYPE_TO_ASSET_TYPE[referencedEntityConfig.type]
                  }
                  url={mediaUrl}
                  title={mediaTitle}
                  description={mediaDescription}
                />
              }
              secondaryText={secondaryText}
              openChooseFromList={openChooseFromList}
              isArrayItem={isArrayItem}
              onClear={onClearEntityReference}
              disabled={!enabled}
              copyItemLabel={formatMessage({ id: 'ID' })}
            />
          ) : null}
          {shouldDisplayError && (
            <StackLayout
              orientation="horizontal"
              spacing="$one"
              horizontalAlignment="spaceBetween"
              verticalAlignment="center"
            >
              <InputStatusMessage inputStatus="error">
                {formatMessage(
                  { id: 'ERROR_LOADING_ENTITY_REFERENCE' },
                  { hasValue: data ? 'true' : 'false', value: data },
                )}
              </InputStatusMessage>
              <Button
                type="button"
                icon="tinyClear"
                hideLabel
                quiet
                priority="tertiary"
                size="toolbar"
                disabled={!enabled}
                onClick={onClearEntityReference}
              >
                {formatMessage({ id: 'CLEAR_ENTITY_REFERENCE_SELECTION' })}
              </Button>
            </StackLayout>
          )}
        </div>

        {shouldDisplayAddImage && (
          <AddItemModalTriggers
            triggerList={addItemTriggerList}
            disabled={!enabled}
          />
        )}
      </Box>
      <AddEntityReferenceFromListModal
        headingLabel={
          <EntitySelectModalHeading domainEntity={referencedEntityConfig} />
        }
        open={isAddingFromList}
        onCancel={closeChooseFromList}
        domainEntity={referencedEntityConfig}
        onSubmit={onSubmitChooseFromList}
        isRelationshipReference={false}
      />
    </>
  );
};

export const MediaReferenceControl: React.FC<EntityFormControlProps> = (
  props,
) => {
  const { schema } = props;
  const imageAssetType = schema['x-entity-asset']?.type as
    | keyof typeof ASSET_TYPE_TO_ENTITY_TYPE
    | undefined;

  const entityType = imageAssetType
    ? ASSET_TYPE_TO_ENTITY_TYPE[imageAssetType]
    : undefined;

  if (!entityType) {
    throw new Error('Entity type is required for media reference control');
  }
  return (
    <EntityFormControl
      {...props}
      renderControl={(controlProps) => (
        <EntityLoader
          entityType={entityType}
          render={(domainEntityConfig) => (
            <MediaReferenceContents
              {...controlProps}
              referencedEntityConfig={domainEntityConfig}
            />
          )}
        />
      )}
    />
  );
};
